import React from "react";
import SEO from "../components/SEO";

const Sponsorship = () => {
  const sponsorshipTiers = [
    {
      title: "Platinum",
      designation: "Founding Sponsor",
      price: "₹2,50,000/-",
      color: "bg-orange-50",
      textColor: "text-orange-600",
      borderColor: "border-orange-300",
      benefits: [
        "Recognition of sponsorship with company logo and website link on the NCLC-2026 website.",
        "One location for a 4×3 banner is provided at the conference venue inside the university campus, as provided by the company.",
        "Half-page advertisement on the last front page of the conference proceedings.",
      ],
    },
    {
      title: "Gold",
      designation: "Principal Sponsor",
      price: "₹2,00,000/-",
      color: "bg-yellow-50",
      textColor: "text-yellow-600",
      borderColor: "border-yellow-400",
      popular: false,
      benefits: [
        "Recognition of sponsorship with company logo and website link on the NCLC-2026 website.",
        "Half-page advertisement on the second page of the conference proceedings.",
      ],
    },
    {
      title: "Silver",
      designation: "Associate Sponsor",
      price: "₹1,50,000/-",
      color: "bg-slate-100",
      textColor: "text-slate-600",
      borderColor: "border-slate-300",
      benefits: [
        "Recognition of sponsorship with company logo on the NCLC-2026 website.",
        "Half-page advertisement on the last second page of the conference proceedings.",
      ],
    },
    {
      title: "Bronze",
      designation: "Supporting Sponsor",
      price: "₹1,00,000/-",
      color: "bg-amber-50",
      textColor: "text-amber-700",
      borderColor: "border-amber-400",
      benefits: ["Conference posters carrying the company logo."],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-16 pt-28 px-4 sm:px-6 lg:px-8">
      <SEO title="Sponsorship" />
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#1e1b4b] mb-6 tracking-tight">
            Partner With Us
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Sponsoring NCLC-2026 provides a unique platform to showcase your
            brand, connect with industry leaders, and engage with top
            researchers and professionals in the field. Choose a tier that fits
            your goals.
          </p>
        </div>

        {/* Why Sponsor Us - Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center transform transition duration-300 hover:-translate-y-2 hover:shadow-md">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Brand Visibility
            </h3>
            <p className="text-gray-600">
              Maximize your brand exposure to a highly targeted audience of
              academic and industry professionals.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center transform transition duration-300 hover:-translate-y-2 hover:shadow-md">
            <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Networking</h3>
            <p className="text-gray-600">
              Engage directly with key decision-makers and build valuable
              relationships during the conference events.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center transform transition duration-300 hover:-translate-y-2 hover:shadow-md">
            <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Thought Leadership
            </h3>
            <p className="text-gray-600">
              Position your organization as a leader by supporting cutting-edge
              research and innovation.
            </p>
          </div>
        </div>

        {/* Sponsorship Packages */}
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-[#1e1b4b]">
            Sponsorship Packages
          </h2>
          <div className="w-24 h-1 bg-indigo-600 mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {sponsorshipTiers.map((tier, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-xl shadow-sm border flex flex-col ${
                tier.popular ? "border-yellow-400 shadow-lg" : "border-gray-200"
              } p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl`}
            >
              {tier.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                  Popular
                </div>
              )}

              {/* Tier Header */}
              <div
                className={`w-full py-3 rounded-lg ${tier.color} border ${tier.borderColor} mb-4 text-center`}
              >
                <h3 className={`text-xl font-bold ${tier.textColor}`}>
                  {tier.title}
                </h3>

                <p
                  className={`text-xs font-medium mt-1 ${tier.textColor} opacity-80`}
                >
                  {tier.designation}
                </p>

                <div className="mt-2">
                  <span className="text-2xl font-extrabold text-gray-900">
                    {tier.price}
                  </span>
                </div>
              </div>

              {/* Benefits */}
              <ul className="space-y-2 flex-1">
                {tier.benefits.map((benefit, bIndex) => (
                  <li key={bIndex} className="flex items-start">
                    <svg
                      className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 ${tier.textColor}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>

                    <span className="text-gray-600 text-xs leading-relaxed">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto pb-16">
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-8 py-5 shadow-sm">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-[#1e1b4b]">
                  Sponsorship Contact
                </h3>
                <p className="text-gray-600 text-sm">
                  For sponsorship opportunities and partnership inquiries.
                </p>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-6">
                <span className="font-semibold text-gray-900">
                  Dr. Pankaj Kumar Tripathi
                </span>
                <a
                  href="mailto:pankaj.tripathi@sharda.ac.in"
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  📧 pankaj.tripathi@sharda.ac.in
                </a>

                <a
                  href="tel:+919956846555"
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  📞 +91 9956846555
                </a>
              </div>
            </div>
          </div>
        </div>

        <section className="mb-20 py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-extrabold text-[#1e1b4b]">
                Our Sponsors
              </h3>

              <div className="w-24 h-1 bg-indigo-600 mx-auto mt-6 rounded-full"></div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {[
                { src: "/nclc-2026/assets/Hexon-300x118.png.webp", alt: "Hexon Logo" },
                { src: "/nclc-2026/assets/rsoc.webp", alt: "Royal Society of Chemistry Logo" },
                { src: "/nclc-2026/assets/iit-roorkee.png", alt: "IIT Roorkee Logo" },
                { src: "/nclc-2026/assets/Picture1.png", alt: "Anusandhan National Research Foundation" },
                { src: "/nclc-2026/assets/Picture2.jpg", alt: "SAAR" },
              ].map((sponsor, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex items-center justify-center w-64 h-32 md:w-72 md:h-36">
                  <img
                    src={sponsor.src}
                    alt={sponsor.alt}
                    className="max-h-20 md:max-h-24 max-w-full object-contain filter hover:brightness-110 transition-all duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom CTA */}
        <div className="bg-[#1e1b4b] rounded-3xl overflow-hidden shadow-2xl relative">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-300 via-[#1e1b4b] to-[#1e1b4b]"></div>
          <div className="relative p-10 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Looking for a Custom Package?
            </h2>
            <p className="text-indigo-200 mb-8 max-w-2xl mx-auto text-lg">
              We also offer customized sponsorship opportunities such as
              Lunch/Dinner sponsorships, Lanyard branding, and more. Let's
              discuss how we can tailor a package to meet your specific needs.
            </p>
            <a
              href="mailto: nclc.sharda2026@gmail.com"
              className="inline-flex items-center px-8 py-4 bg-white text-[#1e1b4b] font-bold rounded-full hover:bg-indigo-50 transition-colors shadow-lg transform hover:scale-105"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              For sponsorship-related inquiries, please contact the Sponsorship
              Team at nclc.sharda2026@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sponsorship;

